import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Barcode from "@/components/Barcode";
import PurchaseDrinkModal from "@/components/PurchaseDrinkModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { typeText } from "@/lib/ticketTypes";
import { Printer, Download, Plus, Lock } from "lucide-react";

const DRINK_LABEL = { SOFT_DRINK: "Soft Drink", BEER: "Beer", COCKTAIL: "Cocktail" };

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [activity, setActivity] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [edit, setEdit] = useState(null);
  const [openBuy, setOpenBuy] = useState(false);

  const load = () =>
    api.get(`/tickets/${id}/history`).then((r) => {
      setT(r.data.ticket); setRedemptions(r.data.redemptions); setActivity(r.data.activity);
      setPurchases(r.data.purchases || []); setEdit({ ...r.data.ticket });
    });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!t) return <div className="text-slate-400">Loading…</div>;

  const isAdmin = user?.role === "admin";
  const canPurchase = (user?.role === "admin" || user?.role === "registration" || user?.role === "reception") && t?.ticket_type !== "EMPLOYEE";

  const save = async () => {
    try {
      const body = {
        guest_name: edit.guest_name, email: edit.email, phone: edit.phone,
        ticket_type: edit.ticket_type, status: edit.status, notes: edit.notes,
      };
      if (isAdmin) {
        body.drinks_allowed = Number(edit.drinks_allowed);
        body.drinks_remaining = Number(edit.drinks_remaining);
      }
      await api.patch(`/tickets/${id}`, body);
      toast.success("Ticket updated"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const adjust = async (delta) => {
    try { await api.post(`/tickets/${id}/adjust`, { delta }); toast.success(delta > 0 ? "Drink added" : "Drink removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (status) => { try { await api.patch(`/tickets/${id}`, { status }); toast.success(`Status: ${status}`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const downloadBarcode = () => {
    const canvas = document.querySelector('[data-testid="pdf417-canvas"]'); if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${t.ticket_id}.png`; link.href = canvas.toDataURL("image/png"); link.click();
  };

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Ticket</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">
          {t.ticket_id} · <span className={typeText(t.ticket_type)} data-testid="ticket-detail-type">{t.ticket_type}</span>
        </h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Printable ticket */}
        <div className="lg:col-span-1">
          <div className="bg-[#FDFBF7] text-slate-900 rounded-2xl border-2 border-[#E2D9C8] p-6 shadow-2xl print:shadow-none" id="print-area">
            <div className="text-center">
              <div className="font-display text-3xl font-black text-slate-900">FOOD BAZAAR</div>
              <div className={`font-display text-lg font-black mt-1 ${t.ticket_type === "VIP" ? "text-rose-600" : t.ticket_type === "EMPLOYEE" ? "text-amber-600" : "text-teal-700"}`}>
                {t.ticket_type}
              </div>
              <div className="text-slate-700 text-sm mt-1">
                {t.base_drinks} INCLUDED DRINK{t.base_drinks > 1 ? "S" : ""}
                {t.additional_drinks > 0 && <> + <b>{t.additional_drinks}</b> additional</>}
              </div>
              {t.guest_name && <div className="mt-2 text-slate-700 text-lg font-semibold">{t.guest_name}</div>}
            </div>
            <Barcode value={t.barcode_token} scale={3} height={20} className="mt-4" />
            <div className="text-center mt-2 font-mono-fb text-slate-700 font-bold">{t.ticket_id}</div>
          </div>
          <div className="mt-3 flex gap-2 print:hidden">
            <Button onClick={() => window.print()} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid="ticket-print-btn">
              <Printer size={16} className="mr-2" /> Print
            </Button>
            <Button onClick={downloadBarcode} variant="outline" className="flex-1 bg-slate-900 border-slate-700 text-slate-200" data-testid="ticket-download-btn">
              <Download size={16} className="mr-2" /> PNG
            </Button>
          </div>
        </div>

        {/* Details / drinks / edit */}
        <div className="lg:col-span-2 space-y-6 print:hidden">
          {/* Drink Allowance panel */}
          <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6">
            <h3 className="font-display text-2xl font-bold text-white mb-4 tracking-tight">DRINK ALLOWANCE</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="p-3 rounded-xl bg-slate-900/70 border border-slate-800">
                <div className="flex items-center gap-1 text-[10px] tracking-widest uppercase text-slate-400 font-bold"><Lock size={10} /> Base</div>
                <div className="font-display text-3xl font-black text-white" data-testid="ticket-base">{t.base_drinks}</div>
                <div className="text-[10px] text-slate-500 mt-1">Included · locked</div>
              </div>
              <div className="p-3 rounded-xl bg-orange-500/10 border border-orange-500/30">
                <div className="text-[10px] tracking-widest uppercase text-orange-300 font-bold">Additional</div>
                <div className="font-display text-3xl font-black text-white" data-testid="ticket-additional">{t.additional_drinks}</div>
                <div className="text-xs text-orange-200 mt-1" data-testid="ticket-credit">Credit ${((t.credit_cents || 0) / 100).toFixed(2)}</div>
                <div className="text-[10px] text-slate-400" data-testid="ticket-credit-history">Bought ${((t.credit_purchased_cents || 0) / 100).toFixed(2)} · Used ${((t.credit_redeemed_cents || 0) / 100).toFixed(2)}</div>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/70 border border-slate-800">
                <div className="text-[10px] tracking-widest uppercase text-slate-400 font-bold">Total</div>
                <div className="font-display text-3xl font-black text-white" data-testid="ticket-total">{t.drinks_allowed}</div>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/70 border border-slate-800">
                <div className="text-[10px] tracking-widest uppercase text-slate-400 font-bold">Redeemed</div>
                <div className="font-display text-3xl font-black text-emerald-400" data-testid="ticket-redeemed">{t.drinks_redeemed}</div>
              </div>
              <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/30 col-span-2 sm:col-span-1">
                <div className="text-[10px] tracking-widest uppercase text-teal-300 font-bold">Remaining</div>
                <div className="font-display text-3xl font-black text-teal-300" data-testid="ticket-remaining">{t.drinks_remaining}</div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {canPurchase && (
                <Button
                  onClick={() => setOpenBuy(true)}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:brightness-110 text-white font-bold"
                  data-testid="add-drinks-btn"
                >
                  <Plus size={16} className="mr-1" /> ADD DRINKS
                </Button>
              )}
            </div>
          </div>

          {/* Ticket details / edit */}
          <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6">
            <h3 className="font-display text-2xl font-bold text-white mb-4 tracking-tight">DETAILS</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs tracking-widest uppercase text-slate-400">Guest Name</Label>
                <Input value={edit.guest_name || ""} onChange={(e) => setEdit({ ...edit, guest_name: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" data-testid="detail-guest-name" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-slate-400">Type</Label>
                <Select value={edit.ticket_type} onValueChange={(v) => setEdit({ ...edit, ticket_type: v })} disabled={!isAdmin}>
                  <SelectTrigger className="bg-[#0B1120] border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="REGULAR">Regular</SelectItem>
                    <SelectItem value="VIP">VIP</SelectItem>
                    <SelectItem value="EMPLOYEE">Employee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs tracking-widest uppercase text-slate-400">Email</Label>
                <Input value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" /></div>
              <div><Label className="text-xs tracking-widest uppercase text-slate-400">Phone</Label>
                <Input value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" /></div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-slate-400">Status</Label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })} disabled={!isAdmin}>
                  <SelectTrigger className="bg-[#0B1120] border-slate-700 text-white" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs tracking-widest uppercase text-slate-400">Notes</Label>
                <Input value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="bg-[#0B1120] border-slate-700 text-white" />
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button onClick={save} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="detail-save-btn">Save Changes</Button>
              {isAdmin && (t.status === "ACTIVE" ? (
                <Button onClick={() => setStatus("INACTIVE")} variant="outline" className="bg-slate-800 border-slate-700 text-slate-200">Deactivate</Button>
              ) : (
                <Button onClick={() => setStatus("ACTIVE")} variant="outline" className="bg-slate-800 border-slate-700 text-slate-200">Reactivate</Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 print:hidden">
        <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6">
          <h3 className="font-display text-xl font-bold text-white mb-3 tracking-tight">REDEMPTION HISTORY</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {redemptions.length === 0 && <div className="text-slate-500 text-sm">No redemptions yet.</div>}
            {redemptions.map((r) => (
              <div key={r.id} className="p-3 rounded-lg bg-[#0B1120] border border-slate-800 text-sm">
                <div className="flex justify-between">
                  <div className="text-white font-bold">{DRINK_LABEL[r.drink_type] || "Drink"}</div>
                  <div className="text-slate-400">{new Date(r.at).toLocaleString()}</div>
                </div>
                <div className="text-xs text-slate-500">−1 · new balance {r.new_balance} · by {r.staff_email}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6">
          <h3 className="font-display text-xl font-bold text-white mb-3 tracking-tight">ADDITIONAL PURCHASES</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {purchases.length === 0 && <div className="text-slate-500 text-sm">No purchases yet.</div>}
            {purchases.map((p) => {
              const items = Array.isArray(p.items) ? p.items : (p.drink_type ? [{ drink_type: p.drink_type, quantity: p.quantity }] : []);
              const total = p.total_quantity || items.reduce((s, it) => s + (it.quantity || 0), 0);
              return (
                <div key={p.id} className="p-3 rounded-lg bg-[#0B1120] border border-orange-500/30 text-sm">
                  <div className="flex justify-between">
                    <div className="text-orange-300 text-[10px] tracking-widest uppercase font-bold">Additional Purchased</div>
                    <div className="text-slate-400">{new Date(p.created_at).toLocaleString()}</div>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {items.map((it, i) => (
                      <div key={i} className="text-white font-semibold">{DRINK_LABEL[it.drink_type]} × {it.quantity}</div>
                    ))}
                  </div>
                  <div className="text-xs mt-1 flex items-center justify-between">
                    <div>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${p.payment_method === "CASH" ? "bg-emerald-500/20 text-emerald-300" : "bg-sky-500/20 text-sky-300"}`}>{p.payment_method}</span>
                      <span className="text-slate-500 ml-2">by {p.created_by_email}</span>
                    </div>
                    <span className="text-orange-300 font-bold">{total} drink{total > 1 ? "s" : ""}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6">
          <h3 className="font-display text-xl font-bold text-white mb-3 tracking-tight">ADMIN ACTIVITY</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {activity.length === 0 && <div className="text-slate-500 text-sm">No admin actions.</div>}
            {activity.map((r) => (
              <div key={r.id} className="p-3 rounded-lg bg-[#0B1120] border border-slate-800 text-sm">
                <div className="flex justify-between">
                  <div className="text-orange-300 font-bold">{r.action}</div>
                  <div className="text-slate-400">{new Date(r.at).toLocaleString()}</div>
                </div>
                <div className="text-xs text-slate-500">by {r.actor_email}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PurchaseDrinkModal
        open={openBuy}
        onClose={() => setOpenBuy(false)}
        ticket={t}
        onSaved={() => { setOpenBuy(false); load(); }}
      />
    </div>
  );
}
